import ReactDOMServer from "react-dom/server";
import { sendCustomMessage } from "../api/socketService";
import { v4 as uuidv4 } from "uuid";

class WebSocketPrintClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.pendingPrintJobs = [];
    this.isProcessingJobs = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 100;
    this.reconnectDelay = 2000;
    this.defaultPort = 3020;
    this.host = "localhost";
    this.isConnecting = false;
    this.reconnectTimer = null;
    this.callbacks = {
      onConnect: null,
      onDisconnect: null,
      onError: null,
      onPrintSuccess: null,
      onPrintError: null,
      onLog: (message) => console.log(`WebSocketPrintClient: ${message}`),
    };
  }

  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  log(message, level = "log") {
    const hasLogCallback = Boolean(this.callbacks.onLog);

    if (hasLogCallback) {
      this.callbacks.onLog(message, level);
    }

    console[level](`WebSocketPrintClient: ${message}`);
  }

  async connect(options = {}) {
    const isAlreadyConnected =
      this.isConnected && this.socket?.readyState === WebSocket.OPEN;

    if (isAlreadyConnected) {
      this.log("Already connected to WebSocket server");
      return true;
    }

    const isCurrentlyConnecting = this.isConnecting;

    if (isCurrentlyConnecting) {
      this.log("Connection attempt already in progress");
      return false;
    }

    return new Promise((resolve, reject) => {
      try {
        this.isConnecting = true;

        const host = options.host || this.host || "localhost";
        const port = options.port || this.defaultPort;
        const url = `ws://${host}:${port}`;

        this.log(`Connecting to WebSocket server at ${url}...`);

        this.closeExistingSocket();

        this.socket = new WebSocket(url);

        this.socket.onopen = () => {
          this.handleSocketOpen();
          resolve(true);
        };

        this.socket.onclose = (event) => {
          this.handleSocketClose(event);
        };

        this.socket.onerror = (error) => {
          this.handleSocketError(error, reject);
        };

        this.socket.onmessage = (event) => {
          this.handleSocketMessage(event);
        };
      } catch (error) {
        this.log(`Connection error: ${error.message}`, "error");
        this.handleConnectionFailure(error);
        reject(error);
      }
    });
  }

  closeExistingSocket() {
    const hasExistingSocket = Boolean(this.socket);

    if (!hasExistingSocket) {
      return;
    }

    try {
      const isConnecting = this.socket.readyState === WebSocket.CONNECTING;
      const isOpen = this.socket.readyState === WebSocket.OPEN;
      const shouldClose = isConnecting || isOpen;

      if (shouldClose) {
        this.socket.close();
      }

      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
    } catch (error) {
      this.log(`Error closing existing socket: ${error.message}`, "warn");
    }

    this.socket = null;
  }

  handleSocketOpen() {
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    this.log("Connected to WebSocket server");

    const hasConnectCallback = Boolean(this.callbacks.onConnect);

    if (hasConnectCallback) {
      this.callbacks.onConnect();
    }

    this.processPendingJobs();
  }

  handleSocketClose(event) {
    const wasConnected = this.isConnected;
    const wasConnecting = this.isConnecting;

    this.isConnected = false;
    this.isConnecting = false;

    const closeCode = event?.code || "unknown";
    const closeReason = event?.reason || "none";

    this.log(
      `Disconnected from WebSocket server (code: ${closeCode}, reason: ${closeReason})`,
      "warn"
    );

    const hasDisconnectCallback = Boolean(this.callbacks.onDisconnect);

    console.log("handleSocketClose - Callback status:", {
      hasCallback: hasDisconnectCallback,
      wasConnected,
      wasConnecting,
      reconnectAttempts: this.reconnectAttempts,
    });

    if (hasDisconnectCallback) {
      console.log("Calling onDisconnect callback...");
      this.callbacks.onDisconnect();
      console.log("onDisconnect callback completed");
    } else {
      console.warn("No onDisconnect callback registered!");
    }

    const shouldAttemptReconnect =
      wasConnected || wasConnecting || this.reconnectAttempts > 0;

    if (shouldAttemptReconnect) {
      this.attemptReconnect();
    }
  }

  handleSocketError(error, reject) {
    this.log(`WebSocket error: ${error?.message || "Unknown error"}`, "error");

    this.isConnecting = false;
    this.isConnected = false;

    const hasErrorCallback = Boolean(this.callbacks.onError);

    if (hasErrorCallback) {
      this.callbacks.onError(error);
    }

    const hasRejectCallback = Boolean(reject);

    if (hasRejectCallback) {
      reject(error);
    }
  }

  handleSocketMessage(event) {
    try {
      const message = JSON.parse(event.data);
      this.handleServerMessage(message);
    } catch (error) {
      this.log(`Error handling server message: ${error.message}`, "error");
    }
  }

  handleConnectionFailure(error) {
    this.isConnected = false;
    this.isConnecting = false;

    const hasErrorCallback = Boolean(this.callbacks.onError);

    if (hasErrorCallback) {
      this.callbacks.onError(error);
    }
  }

  attemptReconnect() {
    const hasReachedMaxAttempts =
      this.reconnectAttempts >= this.maxReconnectAttempts;

    if (hasReachedMaxAttempts) {
      this.log(
        `Maximum reconnect attempts (${this.maxReconnectAttempts}) reached, giving up`,
        "warn"
      );
      return;
    }

    const hasActiveTimer = Boolean(this.reconnectTimer);

    if (hasActiveTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;

    const delay = this.reconnectDelay * this.reconnectAttempts;

    this.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
      "log"
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect({ host: this.host, port: this.defaultPort }).catch(
        (error) => {
          this.log(
            `Reconnect attempt ${this.reconnectAttempts} failed: ${error.message}`,
            "error"
          );
        }
      );
    }, delay);
  }

  disconnect() {
    const hasSocket = Boolean(this.socket);

    if (!hasSocket) {
      return;
    }

    this.log("Disconnecting from WebSocket server...");

    const hasActiveTimer = Boolean(this.reconnectTimer);

    if (hasActiveTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.closeExistingSocket();
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  handleServerMessage(message) {
    this.log(`Received message from server: ${message.type}`);

    const isPrintAcknowledged = message.type === "print_acknowledged";

    if (isPrintAcknowledged) {
      this.handlePrintAcknowledged(message);
      return;
    }

    const isAcknowledged =
      message.type === "shippingRoute_acknowledged" ||
      message.type === "html_acknowledged" ||
      message.type === "shipping_route_html_acknowledged" ||
      message.type === "label_acknowledged";

    if (isAcknowledged) {
      this.handleJobAcknowledged(message);
      return;
    }

    const isCompleted =
      message.type === "print_completed" ||
      message.type === "shippingRoute_completed" ||
      message.type === "html_completed" ||
      message.type === "shipping_route_html_completed" ||
      message.type === "label_completed";

    if (isCompleted) {
      this.handleJobCompleted(message);
      return;
    }

    const isError =
      message.type === "print_error" ||
      message.type === "shippingRoute_error" ||
      message.type === "html_error" ||
      message.type === "shipping_route_html_error" ||
      message.type === "label_error";

    if (isError) {
      this.handleJobError(message);
      return;
    }

    const isServerError = message.type === "error";

    if (isServerError) {
      this.handleServerError(message);
      return;
    }

    this.log(`Unhandled message type: ${message.type}`);
  }

  handlePrintAcknowledged(message) {
    const messagePayload = {
      uniqueueue: uuidv4(),
      type: "order_printed",
      message: message,
    };

    sendCustomMessage(messagePayload);

    const requestId = message.requestId || "unknown";
    this.log(`Job ${requestId} acknowledged by server`);
  }

  handleJobAcknowledged(message) {
    const requestId = message.requestId || "unknown";
    this.log(`Job ${requestId} acknowledged by server`);
  }

  handleJobCompleted(message) {
    const requestId = message.requestId || "unknown";
    this.log(`Job ${requestId} completed successfully`);

    const hasSuccessCallback = Boolean(this.callbacks.onPrintSuccess);

    if (hasSuccessCallback) {
      this.callbacks.onPrintSuccess(message);
    }
  }

  handleJobError(message) {
    const requestId = message.requestId || "unknown";
    const errorMessage = message.error || "Unknown error";

    this.log(`Job ${requestId} failed: ${errorMessage}`, "error");

    const hasErrorCallback = Boolean(this.callbacks.onPrintError);

    if (hasErrorCallback) {
      this.callbacks.onPrintError(message);
    }
  }

  handleServerError(message) {
    this.log(`Server error: ${message.message}`, "error");

    const hasErrorCallback = Boolean(this.callbacks.onError);

    if (hasErrorCallback) {
      this.callbacks.onError(new Error(message.message));
    }
  }

  async sendMessage(message) {
    return new Promise((resolve, reject) => {
      const isNotConnected =
        !this.isConnected ||
        !this.socket ||
        this.socket.readyState !== WebSocket.OPEN;

      if (isNotConnected) {
        reject(new Error("Not connected to WebSocket server"));
        return;
      }

      try {
        this.socket.send(JSON.stringify(message));
        resolve(true);
      } catch (error) {
        this.log(`Error sending message: ${error.message}`, "error");
        reject(error);
      }
    });
  }

  async processPendingJobs() {
    const cannotProcess =
      !this.isConnected ||
      this.isProcessingJobs ||
      this.pendingPrintJobs.length === 0;

    if (cannotProcess) {
      return;
    }

    this.isProcessingJobs = true;

    try {
      const jobCount = this.pendingPrintJobs.length;
      this.log(`Processing ${jobCount} pending jobs`);

      while (this.pendingPrintJobs.length > 0) {
        const job = this.pendingPrintJobs.shift();

        try {
          await this.sendJob(job);
          job.resolve(true);
        } catch (error) {
          this.log(`Failed to process job: ${error.message}`, "error");
          job.reject(error);
        }
      }
    } finally {
      this.isProcessingJobs = false;
    }
  }

  async sendJob(job) {
    const isNotConnected = !this.isConnected;

    if (isNotConnected) {
      throw new Error("Not connected to WebSocket server");
    }

    const messageType = job.messageType || "print";
    const timestamp = Date.now();
    const randomId = Math.floor(Math.random() * 10000);
    const requestId = `${messageType}-${timestamp}-${randomId}`;

    let serverMessageType = messageType;
    const isPrintType = messageType === "print";

    if (isPrintType) {
      serverMessageType = "order";
    }

    const message = {
      type: serverMessageType,
      requestId,
    };

    this.addJobDataToMessage(message, job, messageType);

    this.log(`Sending ${messageType} job ${requestId} to server`);
    await this.sendMessage(message);

    return requestId;
  }

  addJobDataToMessage(message, job, messageType) {
    const isPrintType = messageType === "print";

    if (isPrintType) {
      message.orderData = job.data;
      return;
    }

    const isShippingRouteType = messageType === "shippingRoute";

    if (isShippingRouteType) {
      message.shippingRouteData = job.data;
      return;
    }

    const isLabelType = messageType === "label";

    if (isLabelType) {
      message.labelData = job.data;
      return;
    }

    const isHtmlType =
      messageType === "html" || messageType === "shipping_route_html";

    if (isHtmlType) {
      message.htmlContent = job.data;
      message.printOptions = job.printOptions || {};

      const isShippingRouteHtml = messageType === "shipping_route_html";

      if (isShippingRouteHtml) {
        message.printOptions.type = "shippingRoute";
      }
      return;
    }

    message.data = job.data;
  }

  async printShippingRouteHtml(content, printOptions = {}) {
    try {
      this.log("Preparing to print shipping route HTML content");

      const isNotConnected = !this.isConnected;

      if (isNotConnected) {
        await this.ensureConnection();
      }

      const stillNotConnected = !this.isConnected;

      if (stillNotConnected) {
        return this.queueShippingRouteHtmlJob(content, printOptions);
      }

      const requestId = await this.sendJob({
        messageType: "shipping_route_html",
        data: content,
        printOptions,
      });

      this.log(`Shipping route HTML print job ${requestId} sent successfully`);

      return true;
    } catch (error) {
      this.log(
        `Print error for shipping route HTML content: ${error.message}`,
        "error"
      );
      throw error;
    }
  }

  queueShippingRouteHtmlJob(content, printOptions) {
    return new Promise((resolve, reject) => {
      this.log("Not connected, queueing shipping route HTML print job");
      this.pendingPrintJobs.push({
        messageType: "shipping_route_html",
        data: content,
        printOptions,
        resolve,
        reject,
      });
    });
  }

  async printOrderTicket(orderData) {
    try {
      const isInvalidData = !orderData || !orderData.order;

      if (isInvalidData) {
        throw new Error("Invalid order data");
      }

      const orderId = orderData.order.id || "unknown";
      this.log(`Preparing to print order ticket ${orderId}`);

      const isNotConnected = !this.isConnected;

      if (isNotConnected) {
        await this.ensureConnection();
      }

      const stillNotConnected = !this.isConnected;

      if (stillNotConnected) {
        return this.queueOrderTicketJob(orderData, orderId);
      }

      const requestId = await this.sendJob({
        messageType: "print",
        data: orderData,
      });

      this.log(`Print job ${requestId} sent successfully for order ${orderId}`);

      return true;
    } catch (error) {
      this.log(`Print error for order ticket: ${error.message}`, "error");
      throw error;
    }
  }

  queueOrderTicketJob(orderData, orderId) {
    return new Promise((resolve, reject) => {
      this.log(`Not connected, queueing print job for order ${orderId}`);
      this.pendingPrintJobs.push({
        messageType: "print",
        data: orderData,
        resolve,
        reject,
      });
    });
  }

  async printShippingRoute(shippingRouteData) {
    try {
      const isInvalidData = !shippingRouteData || !shippingRouteData.route;

      if (isInvalidData) {
        throw new Error("Invalid shipping route data");
      }

      const routeId = shippingRouteData.route.id || "unknown";
      this.log(`Preparing to print shipping route ${routeId}`);

      const isNotConnected = !this.isConnected;

      if (isNotConnected) {
        await this.ensureConnection();
      }

      const stillNotConnected = !this.isConnected;

      if (stillNotConnected) {
        return this.queueShippingRouteJob(shippingRouteData, routeId);
      }

      const requestId = await this.sendJob({
        messageType: "shippingRoute",
        data: shippingRouteData,
      });

      this.log(
        `Shipping route job ${requestId} sent successfully for route ${routeId}`
      );

      return true;
    } catch (error) {
      this.log(`Print error for shipping route: ${error.message}`, "error");
      throw error;
    }
  }

  queueShippingRouteJob(shippingRouteData, routeId) {
    return new Promise((resolve, reject) => {
      this.log(`Not connected, queueing print job for route ${routeId}`);
      this.pendingPrintJobs.push({
        messageType: "shippingRoute",
        data: shippingRouteData,
        resolve,
        reject,
      });
    });
  }

  async printLabel(labelData, quantity = 1) {
    try {
      const isInvalidData = !labelData || !labelData.id;

      if (isInvalidData) {
        throw new Error("Invalid label data");
      }

      const labelId = labelData.id;
      this.log(`Preparing to print label ${labelId} (quantity: ${quantity})`);

      const isNotConnected = !this.isConnected;

      if (isNotConnected) {
        await this.ensureConnection();
      }

      const stillNotConnected = !this.isConnected;

      if (stillNotConnected) {
        return this.queueLabelJob(labelData, quantity, labelId);
      }

      const requestId = await this.sendJob({
        messageType: "label",
        data: {
          label: labelData,
          quantity: quantity,
        },
      });

      this.log(
        `Label print job ${requestId} sent successfully for label ${labelId}`
      );

      return true;
    } catch (error) {
      this.log(`Print error for label: ${error.message}`, "error");
      throw error;
    }
  }

  queueLabelJob(labelData, quantity, labelId) {
    return new Promise((resolve, reject) => {
      this.log(`Not connected, queueing print job for label ${labelId}`);
      this.pendingPrintJobs.push({
        messageType: "label",
        data: {
          label: labelData,
          quantity: quantity,
        },
        resolve,
        reject,
      });
    });
  }

  async ensureConnection() {
    try {
      await this.connect();
    } catch (error) {
      this.log(
        `Failed to connect to WebSocket server: ${error.message}`,
        "error"
      );
      throw new Error(
        `Cannot print: WebSocket server connection failed: ${error.message}`
      );
    }
  }

  async printHtml({ content, type = "order", printOptions = {} }) {
    try {
      this.log(`Preparing to print HTML content (type: ${type})`);

      const isHtmlType = type === "html";

      if (isHtmlType) {
        return await this.printHtmlDirectly(content, printOptions);
      }

      const isShippingRouteType = type === "shippingRoute";

      if (isShippingRouteType) {
        const shippingRouteData = this.createShippingRouteData(content);
        return await this.printShippingRoute(shippingRouteData);
      }

      const orderData = this.createOrderData(content, type);
      return await this.printOrderTicket(orderData);
    } catch (error) {
      this.log(`Print error for HTML content: ${error.message}`, "error");
      throw error;
    }
  }

  createShippingRouteData(content) {
    return {
      route: {
        id: `html-print-${Date.now()}`,
        type: "shippingRoute",
        created_at: new Date().toISOString(),
      },
      deliveries: [],
      htmlContent: content,
    };
  }

  createOrderData(content, type) {
    return {
      order: {
        id: `html-print-${Date.now()}`,
        type: type,
        created_at: new Date().toISOString(),
      },
      products: [],
      htmlContent: content,
    };
  }

  async printHtmlDirectly(content, printOptions = {}) {
    try {
      this.log("Preparing to print direct HTML content");

      const isNotConnected = !this.isConnected;

      if (isNotConnected) {
        await this.ensureConnection();
      }

      const stillNotConnected = !this.isConnected;

      if (stillNotConnected) {
        return this.queueHtmlJob(content, printOptions);
      }

      const requestId = await this.sendJob({
        messageType: "html",
        data: content,
        printOptions,
      });

      this.log(`HTML print job ${requestId} sent successfully`);

      return true;
    } catch (error) {
      this.log(`Print error for HTML content: ${error.message}`, "error");
      throw error;
    }
  }

  queueHtmlJob(content, printOptions) {
    return new Promise((resolve, reject) => {
      this.log("Not connected, queueing HTML print job");
      this.pendingPrintJobs.push({
        messageType: "html",
        data: content,
        printOptions,
        resolve,
        reject,
      });
    });
  }

  async printReactComponent({ component, type = "order", printOptions = {} }) {
    try {
      this.log("Rendering React component to HTML");

      const htmlContent = ReactDOMServer.renderToString(component);

      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
                body {
                    margin: 0;
                    padding: 8px;
                    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    font-size: 12px;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                @media print {
                    html, body {
                        width: 100%;
                        margin: 0;
                        padding: 0;
                    }
                }
            </style>
        </head>
        <body>
            ${htmlContent}
        </body>
        </html>
      `;

      return await this.printHtml({
        content: fullHtml,
        type,
        printOptions,
      });
    } catch (error) {
      this.log(`Print error for React component: ${error.message}`, "error");
      throw error;
    }
  }
}

const websocketPrintClient = new WebSocketPrintClient();
export default websocketPrintClient;
